import { type QueryClient, useQuery } from "@tanstack/react-query";
import { getThreadMessages } from "controller/api/chat/chat";
import { generateQueryKey } from "controller/cache/helpers/queryKeyGenerator";
import type { GetThreadMessagesResponse, ThreadMessage } from "shared";

export const threadMessagesKeys = {
  all: () => generateQueryKey({ entity: "thread-messages" }),
  detail: (threadId: string, page?: number) =>
    generateQueryKey({
      entity: "thread-messages",
      operation: "detail",
      filters: { threadId },
      params: { page },
    }),
};

export const useGetThreadMessages = (
  threadId: string | null | undefined,
  page: number = 1,
  limit: number = 20,
) => {
  return useQuery<GetThreadMessagesResponse, Error>({
    queryKey: threadMessagesKeys.detail(threadId ?? "", page),
    queryFn: () =>
      getThreadMessages({
        threadId: threadId!,
        page,
        limit,
      }),
    enabled: Boolean(threadId),
  });
};

/**
 * Append a new thread reply to the cached thread messages.
 * Used when an MQTT message arrives for an open thread.
 */
export function addThreadReplyToCache(
  queryClient: QueryClient,
  threadId: string,
  reply: ThreadMessage,
) {
  queryClient.setQueryData<GetThreadMessagesResponse>(
    threadMessagesKeys.detail(threadId, 1),
    (prev) => {
      if (!prev) return prev;
      const alreadyExists = prev.data.messages.some(
        (m) => m.chat_id === reply.chat_id,
      );
      if (alreadyExists) return prev;
      return {
        ...prev,
        data: {
          ...prev.data,
          messages: [...prev.data.messages, reply],
        },
        pagination: {
          ...prev.pagination,
          total: prev.pagination.total + 1,
        },
      };
    },
  );
}
